import { memo, useEffect, useState } from "react";
import type { Scene, Shot, Shotliste } from "../state/shotlist";
import { bilde as hentBilde } from "../state/shotlist";
import { tap as hapticTap } from "../components/haptics";

/**
 * Shotlista: det foto skal ha i boks.
 *
 * SCENENE ER SEKSJONER, ikke en kolonne. Paa nettflata ligger de til venstre
 * fordi det er plass; her er det én skjermbredde, og da er det raskere aa
 * bla enn aa velge. Rekkefoelgen er den A-foto satte.
 *
 * INGEN TIDSKODE. Et bilde som er tatt er tatt — NAAR det ble tatt staar i
 * loggen, og en tidskode her ville gjort shotlista til en logg nummer to.
 *
 * DET SOM ER GJORT BLIR STAAENDE. Linja forsvinner ikke naar den hukes av,
 * den dempes. En liste som toemmer seg selv gir ingen oversikt paa slutten
 * av dagen.
 *
 * HAKEN ER DELT. Huker én av, ser hele crewet det. Den skrives lokalt foerst
 * og gaar opp naar dekningen kommer.
 *
 * LISTA SETTES OPP PAA NETTFLATA. Det er det eneste feltet her som ikke kan
 * skrives i fra telefonen, og det er med vilje: en shotlist er en plan noen
 * har lagt, ikke en huskeliste man fyller paa i farten. Kommer man paa noe,
 * staar det i loggen.
 */
export const ShotlistScreen = memo(function ShotlistScreen({
  liste, bildeklar, onHake,
}: {
  readonly liste: Shotliste;
  /** Oekes naar nye referansebilder er lastet ned. Se App.tsx. */
  readonly bildeklar: number;
  onHake(shotId: string, gjort: boolean): void;
}): JSX.Element {
  if (liste.scener.length === 0) {
    return (
      <div className="surface surface--rull" style={{ height: "100%" }}>
        <div className="beskjed">
          <span className="beskjed__tom-tittel">Ingen shotlist i dag</span>
          <span className="beskjed__tom mono">
            A-foto, redaktør og produsent setter den opp fra nettsiden. Er
            den lagt inn, står den her — også uten dekning.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="surface surface--rull" style={{ height: "100%" }}>
      <div className="beskjed">
        {liste.scener.map((s) => (
          <Scenebolk
            key={s.id}
            scene={s}
            shots={liste.shots.filter((b) => b.sceneId === s.id)}
            bildeklar={bildeklar}
            onHake={onHake}
          />
        ))}
      </div>
    </div>
  );
});

function Scenebolk({
  scene, shots, bildeklar, onHake,
}: {
  readonly scene: Scene;
  readonly shots: readonly Shot[];
  readonly bildeklar: number;
  onHake(shotId: string, gjort: boolean): void;
}): JSX.Element {
  const igjen = shots.filter((b) => !b.gjort).length;
  return (
    <section className="beskjed__bolk">
      <h2 className="beskjed__til">{scene.navn}</h2>
      {/* Tallet er DET SOM GJENSTAAR. Det er det man spoer om naar man ser
          paa en liste man skal gjennom. */}
      <span className="beskjed__naar mono">
        {igjen === 0 ? "alt i boks" : `${igjen} igjen`}
      </span>

      {shots.map((b) => (
        <div key={b.id} className="shotlinje">
          <button
            type="button"
            className={`beskjed__huske${b.gjort ? " beskjed__huske--gjort" : ""}`}
            onClick={() => { hapticTap(); onHake(b.id, !b.gjort); }}
            aria-pressed={b.gjort}
          >
            <span className="beskjed__boks" aria-hidden="true">
              {b.gjort ? "✓" : ""}
            </span>
            <span>{b.tekst}</span>
          </button>

          {/* Kommentaren staar som broedtekst under hovedlinja, ikke bak et
              trykk. Den er ofte det eneste som sier HVORDAN bildet skal tas. */}
          {b.notat.length > 0 ? <p className="shotlinje__notat">{b.notat}</p> : null}
          {b.bildeSti === null ? null : (
            <Referansebilde key={`${b.bildeSti}:${bildeklar}`} sti={b.bildeSti} />
          )}
        </div>
      ))}
    </section>
  );
}

/**
 * Referansebildet, lest fra disk.
 *
 * Ligger i OPFS som dokumentene og vises derfra: bildet man trenger naar man
 * rigger skal ikke vaere det ene som krever dekning. Er det ikke lastet ned
 * ennaa, staar det ingenting — ikke en tom ramme som ser ut som en feil.
 */
function Referansebilde({ sti }: { readonly sti: string }): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      const u = await hentBilde(sti);
      if (!avbrutt) setUrl(u);
    })();
    return () => { avbrutt = true; };
  }, [sti]);
  if (url === null) return null;
  return <img className="shotlinje__bilde" src={url} alt="Referansebilde" />;
}
